import { NavLink } from 'react-router-dom'
import { useTagGroups } from '@/api/queries'
import { tagPath } from '@/api/taxonomy'
import { ChevronRightIcon, CollapseIcon } from '@/components/icons'
import { AdSlot, Panel, Skeleton } from '@/components/ui'
import { cn } from '@/utils/cn'

/**
 * 侧栏导航（设计稿 D1/D2 左栏，M2 抽屉内同一份内容）。
 *
 * 结构：标题「导航」+ 折叠按钮，下面是所有标签铺成的一列条目（不展示
 * `category` 分组标题这一层根节点，只展示标签本身这一层叶节点）；
 * 底部一个广告位。
 *
 * 标签**完全来自后端**：`GET /tags` 的 `category` 字段（类型 / 游戏 / 其他）
 * 只用来取数据，不再渲染成分组标题。单独维护一棵前端分类树只会和后端漂移 ——
 * 这曾经是 docs/backend-sync/2026-08-07-tag-facets-diff.md 里最主要的差异，
 * 后端补上 category 之后就没有前端名单了。
 *
 * 每个条目落到 `/t/{public_id}`，由 `GET /tags/{public_id}/posts` 出数据。
 */

export interface SideNavProps {
  /** 折叠态：设计稿左上角那个 `‹` 按钮的效果。 */
  collapsed?: boolean
  onToggleCollapse?: () => void
  /** 抽屉里用：不需要外层 Panel 与折叠按钮。 */
  variant?: 'panel' | 'bare'
  /** 点击条目后的回调，抽屉用它关闭自己。 */
  onNavigate?: () => void
  className?: string
}

export function SideNav({
  collapsed = false,
  onToggleCollapse,
  variant = 'panel',
  onNavigate,
  className,
}: SideNavProps) {
  const bare = variant === 'bare'
  const { groups, isPending } = useTagGroups()

  const body = (
    <>
      {!bare && (
        <div className="flex items-center justify-between gap-2 pb-1">
          <p className="text-xs font-medium text-subtle-foreground">导航</p>
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={collapsed ? '展开导航' : '收起导航'}
              aria-expanded={!collapsed}
              className="rounded-lg p-1 text-subtle-foreground transition-colors hover:bg-surface-muted hover:text-foreground"
            >
              {collapsed ? (
                <ChevronRightIcon className="size-4" />
              ) : (
                <CollapseIcon className="size-4" />
              )}
            </button>
          )}
        </div>
      )}

      {!collapsed && isPending && (
        // 骨架屏行数与常见的分组规模接近，避免加载完成时整个左栏跳一下。
        <div className={cn('space-y-2', bare && 'p-4')} aria-hidden="true">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-5 w-full" />
          ))}
        </div>
      )}

      {!collapsed && !isPending && (
        <nav aria-label="站点分类" className={cn(bare && 'p-4')}>
          <ul className="space-y-0.5">
            {groups.flatMap((section) => section.tags).map((item) => (
              <li key={item.public_id}>
                <NavLink
                  to={tagPath(item)}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-2 rounded-lg py-1.5 pl-1 pr-2 text-xl transition-colors',
                      isActive
                        ? 'font-semibold text-primary'
                        : 'font-medium text-foreground hover:text-primary',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden="true"
                        className={cn(
                          'size-1.5 shrink-0 transition-colors',
                          isActive ? 'bg-primary' : 'bg-accent group-hover:bg-primary',
                        )}
                      />
                      {item.name}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  )

  if (bare) return <div className={className}>{body}</div>

  return (
    <div className={cn('space-y-3', className)}>
      <Panel className="space-y-3">{body}</Panel>
      {!collapsed && <AdSlot />}
    </div>
  )
}
